import { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OrçamentosContext } from 'contexts/OrçamentosContext';
import { DropsContext } from 'contexts/DropsContext';
import { GridContext } from 'contexts/GridContext';
import { useModal } from 'components/Modals';
import ResumoModal from 'components/Modals/ResumoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import api from 'services/api';
import columnOverrides from './columnOverrides';
import getOrçamentosFilterFields from './filterFields';

const Orçamentos = () => {
  const rotina = 'Orçamentos';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const { drops } = useContext(DropsContext);
  const { exportPdf, exportXls } = useContext(GridContext);
  const { orçamentos, getOrçamentos, getLoading } =
    useContext(OrçamentosContext);

  const filterFields = useMemo(() => getOrçamentosFilterFields(drops), [drops]);

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const loadGrid = (props = {}) => {
    const filter = props?.filter || getValues();

    closeModal();

    getOrçamentos({ ...props, rotina, filter });
  };

  const print = async (id) => {
    const params = { id };
    const { data } = await api.get(`/Relatorios/Gerar/Documento`, { params });
    const urls = data?.urls || [];
    urls?.forEach((url) => window.open(url, '_blank'));
  };

  const toolbarActions = [
    {
      name: 'Adicionar Orçamento',
      icon: 'add',
      placement: 'primary',
      action: () => navigate('/app/Orcamentos/Gerar'),
    },
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({ rotina, filter: getValues(), order: orçamentos?.order }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({ rotina, filter: getValues(), order: orçamentos?.order }),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={orçamentos?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getValues() },
              })
            }
          />
        ),
    },
  ];

  const rowActions = [
    {
      name: 'Aprovação necessária',
      icon: 'priority_high',
      color: 'secondary.main',
      show: ({ row }) => row?.aprovacao === 'PENDENTE',
    },
    {
      name: 'Aprovado',
      icon: 'check',
      color: 'primary.main',
      show: ({ row }) => row?.aprovacao === 'SIM',
    },
    {
      name: ' Reprovado',
      icon: 'close',
      color: 'secondary.main',
      show: ({ row }) => row?.aprovacao === 'NAO',
    },
    {
      name: 'Detalhes',
      icon: 'visibility',
      action: ({ row }) => navigate(`/app/Documento/${row?.id}`),
    },
    {
      name: 'Imprimir',
      icon: 'print',
      action: ({ row }) => print(row?.id),
    },
  ];

  return (
    <Container fillHeight>
      <Header rotina={rotina} colunas={orçamentos?.colunas} />
      <Card fillHeight>
        <DataView
          fillHeight
          grid={orçamentos}
          filterFields={filterFields}
          filterForm={filterForm}
          onFilter={applyFilter}
          columnOverrides={columnOverrides}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          checkboxSelection
          loading={getLoading}
          loadGrid={loadGrid}
        />
      </Card>
    </Container>
  );
};

export default Orçamentos;
